import { useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useState } from "react";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { BrandMark } from "../components/BrandMark";
import { PublicMobileMenu } from "../components/PublicMobileMenu";
import { OFFICE_HOURS_LINES } from "../lib/office-hours";
import { toast } from "sonner";
import {
    Menu, X, ArrowRight, Wallet, Briefcase, Users, PiggyBank,
    TrendingUp, ShieldCheck, FileText, Bell, CheckCircle2,
    Sparkles, Phone, Mail, MapPin, Facebook, Instagram, Linkedin, MessageCircle,
    Heart, HandCoins, HelpCircle, Clock,
} from "lucide-react";

/* ---------- data ---------- */
const NAV = [
    { label: "Home", href: "#top" },
    { label: "About Us", to: "/about-us" },
    { label: "Loans", to: "/loans" },
    { label: "Saving Scheme", to: "/daily-savings" },
    { label: "Investment", to: "/investment" },
    { label: "Careers", to: "/careers" },
    { label: "Contact Us", to: "/contact-us" },
];

const TRUST = [
    { icon: Sparkles, title: "Simple Processes" },
    { icon: ShieldCheck, title: "Secure Digital Records" },
    { icon: FileText, title: "Transparent Account Information" },
    { icon: Heart, title: "Customer-focused Support" },
];

const BAND = [
    { icon: HandCoins, title: "Customer-focused Service" },
    { icon: FileText, title: "Clear Account Records" },
    { icon: Sparkles, title: "Flexible Product Options" },
    { icon: Bell, title: "Digital Access" },
];

const CONTACT_NOTE = "Contact your nearest Lokdhara branch for more information.";

const PRODUCTS = [
    {
        icon: Wallet,
        title: "Personal Loan",
        desc: "Financial support for approved personal requirements.",
        details: [
            "Loan for approved personal financial needs.",
        ],
    },
    {
        icon: Briefcase,
        title: "Business Loan",
        desc: "Funding solutions for approved business and working-capital needs.",
        details: [
            "Funding for eligible business / working-capital needs.",
        ],
    },
    {
        icon: Users,
        title: "Group Loan",
        desc: "Structured financial support for eligible joint-liability groups.",
        details: [
            "Joint-liability loan for eligible groups of 5–10 members.",
        ],
    },
    {
        icon: PiggyBank,
        title: "Saving Scheme",
        desc: "A flexible way to build savings through regular contributions.",
        details: [
            "Builds a regular savings habit.",
            "Rates:",
            "• Under 180 days — 0 % interest + 3 % early-withdrawal penalty",
            "• 180 – 364 days — 3 % p.a.",
            "• 365 days — 5 % p.a.",
        ],
    },
    {
        icon: TrendingUp,
        title: "Investment",
        desc: "A defined-tenure investment option subject to approved terms.",
        details: [
            "365-day investment; 12 % p.a. simple interest paid at maturity.",
            "Closed before 365 days: principal is returned with 0 % interest.",
        ],
    },
];

const PORTAL_FEATURES = [
    "View approved accounts",
    "Access statements and receipts",
    "Track repayments and savings",
    "Receive important notifications",
];

const VALUES = [
    { icon: FileText, title: "Clear Information",
        desc: "Straightforward account records, receipts and statements." },
    { icon: Sparkles, title: "Simple Account Access",
        desc: "A calm, single place for every product you hold with us." },
    { icon: HelpCircle, title: "Responsive Support",
        desc: "Reach a human when it matters — from any device." },
];

const FOOTER_LINKS = {
    Products: ["Personal Loan", "Business Loan", "Group Loan", "Saving Scheme", "Investment"],
    Company: ["About Us", "Careers", "News & Updates", "Contact Us"],
    Support: ["FAQs", "Grievance Redressal", "Customer Care", "Downloads"],
};

/* ---------- primitives ---------- */
const scrollTo = (id) => {
    if (!id?.startsWith("#")) return;
    const el = document.querySelector(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

const placeholderLoginToast = () => {
    window.location.assign("/customer-login");
};

/* ---------- page ---------- */
export default function PublicHome() {
    const [menuOpen, setMenuOpen] = useState(false);
    const [openProducts, setOpenProducts] = useState(() => new Set());
    const toggleProduct = (title) => setOpenProducts((prev) => {
        const next = new Set(prev);
        if (next.has(title)) next.delete(title); else next.add(title);
        return next;
    });
    const navigate = useNavigate();

    useEffect(() => {
        document.title = "Lokdhara Microfinance | Financial Solutions that Empower";
    }, []);

    const handleNavClick = (n) => {
        if (n.to) navigate(n.to);
        else scrollTo(n.href);
    };

    return (
        <div id="top" className="min-h-screen bg-white text-slate-900 antialiased">
            {/* HEADER ------------------------------------------------------- */}
            <header className="sticky top-0 z-40 bg-white/95 backdrop-blur border-b border-slate-100">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 h-20 flex items-center justify-between gap-4">
                    <a href="#top" className="flex items-center" data-testid="home-brand">
                        <BrandMark size={56} className="sm:!h-14" />
                    </a>
                    <nav className="hidden lg:flex items-center gap-6 text-sm font-medium text-slate-700">
                        {NAV.map((n) => (
                            <button key={n.label}
                                onClick={() => handleNavClick(n)}
                                className="hover:text-brand-royal transition"
                                data-testid={`nav-${n.label.replace(/\s+/g, "-").toLowerCase()}`}
                            >
                                {n.label}
                            </button>
                        ))}
                    </nav>
                    <div className="flex items-center gap-2">
                        <Button
                            className="hidden sm:inline-flex bg-brand-royal hover:bg-brand-hover text-white rounded-full h-10 px-5"
                            onClick={placeholderLoginToast}
                            data-testid="header-customer-login"
                        >
                            Customer Login
                        </Button>
                        <button
                            aria-label="Open menu"
                            className="lg:hidden h-10 w-10 rounded-lg border border-slate-200 grid place-items-center"
                            onClick={() => setMenuOpen(true)}
                            data-testid="mobile-menu-btn"
                        >
                            <Menu className="h-5 w-5" />
                        </button>
                    </div>
                </div>
                {/* mobile drawer */}
                <PublicMobileMenu
                    open={menuOpen}
                    onOpenChange={setMenuOpen}
                    nav={NAV}
                    onNavItemClick={handleNavClick}
                    onLoginClick={placeholderLoginToast}
                    testId="mobile-menu"
                />
            </header>

            {/* HERO --------------------------------------------------------- */}
            <section className="relative overflow-hidden bg-gradient-to-br from-white via-brand-soft/40 to-brand-tint/50">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 py-16 lg:py-24 grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
                    <div>
                        <span className="inline-block text-xs font-semibold uppercase tracking-widest text-brand-royal bg-brand-soft rounded-full px-3 py-1">
                            Empowering Financial Progress
                        </span>
                        <h1 className="mt-5 font-display text-4xl sm:text-5xl lg:text-6xl font-bold text-brand-navy leading-tight tracking-tight">
                            Financial Solutions <span className="text-brand-royal">that Empower.</span>
                        </h1>
                        <p className="mt-5 text-base sm:text-lg text-slate-600 max-w-xl leading-relaxed">
                            Simple, transparent and accessible financial solutions for individuals,
                            businesses and communities.
                        </p>
                        <div className="mt-8 flex flex-wrap gap-3">
                            <Button
                                className="bg-brand-royal hover:bg-brand-hover text-white rounded-full h-12 px-6 text-base"
                                onClick={() => scrollTo("#products")}
                                data-testid="hero-explore-btn"
                            >
                                Explore Our Products <ArrowRight className="h-4 w-4 ml-2" />
                            </Button>
                            <Button variant="outline"
                                className="rounded-full h-12 px-6 text-base border-slate-300"
                                onClick={placeholderLoginToast}
                                data-testid="hero-customer-login"
                            >
                                Customer Login
                            </Button>
                        </div>
                    </div>
                    <div className="relative">
                        <div className="absolute -inset-4 bg-brand-tint/40 rounded-[2rem] -rotate-2" aria-hidden />
                        <img
                            src="/home-hero.jpg"
                            alt="Financial advisor reviewing loan documents with a customer at an office desk"
                            className="relative rounded-[1.75rem] shadow-card-hover w-full h-auto object-contain bg-brand-tint/30"
                            loading="eager"
                        />
                    </div>
                </div>
            </section>

            {/* TRUST STRIP ------------------------------------------------- */}
            <div className="max-w-7xl mx-auto px-4 sm:px-6 -mt-8 relative z-10">
                <Card className="rounded-2xl shadow-card border-slate-100 p-5 sm:p-7 grid grid-cols-2 md:grid-cols-4 gap-6">
                    {TRUST.map(({ icon: Icon, title }) => (
                        <div key={title} className="flex items-start gap-3">
                            <div className="h-10 w-10 rounded-xl bg-brand-soft grid place-items-center shrink-0">
                                <Icon className="h-5 w-5 text-brand-royal" strokeWidth={1.75} />
                            </div>
                            <div className="text-sm font-semibold text-slate-800 leading-snug">{title}</div>
                        </div>
                    ))}
                </Card>
            </div>

            {/* DEEP NAVY BAND ---------------------------------------------- */}
            <section className="mt-20 bg-brand-navy text-white">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 py-14 grid grid-cols-2 lg:grid-cols-4 gap-8">
                    {BAND.map(({ icon: Icon, title }) => (
                        <div key={title} className="flex items-center gap-3">
                            <div className="h-11 w-11 rounded-xl bg-white/10 grid place-items-center shrink-0">
                                <Icon className="h-5 w-5 text-brand-tint" strokeWidth={1.75} />
                            </div>
                            <div className="text-sm sm:text-base font-semibold leading-snug">{title}</div>
                        </div>
                    ))}
                </div>
            </section>

            {/* PRODUCTS ---------------------------------------------------- */}
            <section id="products" className="py-20 sm:py-24">
                <div className="max-w-7xl mx-auto px-4 sm:px-6">
                    <div className="text-center max-w-2xl mx-auto mb-14">
                        <h2 className="font-display text-3xl sm:text-4xl font-bold text-brand-navy tracking-tight">
                            Our Financial Solutions
                        </h2>
                        <p className="mt-3 text-slate-600">
                            Designed to support different personal, business and community needs.
                        </p>
                    </div>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-5">
                        {PRODUCTS.map(({ icon: Icon, title, desc, details }) => {
                            const isOpen = openProducts.has(title);
                            return (
                                <Card key={title}
                                    className="rounded-2xl border-slate-100 p-6 hover:shadow-card-hover transition group"
                                    data-testid={`product-card-${title.replace(/\s+/g, "-").toLowerCase()}`}
                                >
                                    <div className="h-12 w-12 rounded-xl bg-brand-soft group-hover:bg-brand-tint transition grid place-items-center">
                                        <Icon className="h-5 w-5 text-brand-royal" strokeWidth={1.75} />
                                    </div>
                                    <h3 className="mt-5 font-semibold text-slate-900 text-base">{title}</h3>
                                    <p className="mt-2 text-sm text-slate-600 leading-relaxed min-h-[64px]">{desc}</p>
                                    <button
                                        type="button"
                                        aria-expanded={isOpen}
                                        onClick={() => toggleProduct(title)}
                                        data-testid={`product-learn-more-${title.replace(/\s+/g, "-").toLowerCase()}`}
                                        className="mt-4 text-sm font-semibold text-brand-royal hover:text-brand-hover inline-flex items-center gap-1"
                                    >
                                        {isOpen ? "Show less" : "Learn More"}
                                        <ArrowRight
                                            className={`h-3.5 w-3.5 transition-transform ${isOpen ? "rotate-90" : ""}`}
                                        />
                                    </button>
                                    {isOpen && (
                                        <div
                                            className="mt-4 pt-4 border-t border-slate-100 text-sm text-slate-700 leading-relaxed space-y-1"
                                            data-testid={`product-details-${title.replace(/\s+/g, "-").toLowerCase()}`}
                                        >
                                            {details.map((line) => (
                                                <p key={line}>{line}</p>
                                            ))}
                                            <p className="pt-2 text-slate-500 text-xs">
                                                {CONTACT_NOTE}
                                            </p>
                                        </div>
                                    )}
                                </Card>
                            );
                        })}
                    </div>
                    <p
                        className="mt-6 text-right text-[11px] text-slate-500 italic"
                        data-testid="product-terms-note"
                    >
                        * Terms &amp; Conditions apply.
                    </p>
                </div>
            </section>

            {/* DIGITAL PORTAL ---------------------------------------------- */}
            <section className="bg-brand-soft py-20 sm:py-24">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 grid lg:grid-cols-2 gap-14 items-center">
                    <div>
                        <span className="inline-block text-xs font-semibold uppercase tracking-widest text-brand-royal">
                            Digital Portal
                        </span>
                        <h2 className="mt-3 font-display text-3xl sm:text-4xl font-bold text-brand-navy tracking-tight leading-tight">
                            One secure place for your financial journey.
                        </h2>
                        <ul className="mt-6 space-y-3">
                            {PORTAL_FEATURES.map((f) => (
                                <li key={f} className="flex items-start gap-3">
                                    <CheckCircle2 className="h-5 w-5 text-brand-royal shrink-0 mt-0.5" />
                                    <span className="text-slate-700">{f}</span>
                                </li>
                            ))}
                        </ul>
                        <p className="mt-6 text-xs text-slate-500 italic">
                            The customer portal is currently rolling out. Please contact your relationship
                            manager for early access.
                        </p>
                    </div>
                    {/* Portal mock-up — generic, no customer-specific data */}
                    <div className="relative">
                        <div className="hidden sm:block absolute inset-0 bg-brand-tint/40 rounded-[2rem] blur-2xl" aria-hidden />
                        <div className="relative bg-white rounded-3xl border border-slate-100 shadow-card-hover p-4 sm:p-6">
                            <div className="flex items-center gap-2 pb-3 border-b border-slate-100">
                                <BrandMark size={22} />
                                <div className="text-xs font-mono text-slate-400 ml-auto">portal.lokdhara</div>
                            </div>
                            <div className="mt-4 grid grid-cols-3 gap-3">
                                {[
                                    { label: "Loans", v: "Track" },
                                    { label: "Savings", v: "Grow" },
                                    { label: "Investments", v: "Manage" },
                                ].map((k) => (
                                    <div key={k.label} className="bg-brand-soft rounded-xl p-3">
                                        <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">{k.label}</div>
                                        <div className="mt-1 font-display font-bold text-brand-navy text-sm">{k.v}</div>
                                    </div>
                                ))}
                            </div>
                            <div className="mt-4 space-y-2">
                                {["Statements & Passbook", "Withdrawal Requests", "Complaints & Support"].map((r) => (
                                    <div key={r} className="flex items-center justify-between bg-slate-50 rounded-xl px-3 py-2.5 text-sm">
                                        <span className="text-slate-700 font-medium">{r}</span>
                                        <span className="text-xs text-brand-royal font-semibold">Explore</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* CUSTOMER VALUES --------------------------------------------- */}
            <section className="py-20 sm:py-24">
                <div className="max-w-7xl mx-auto px-4 sm:px-6">
                    <div className="text-center max-w-2xl mx-auto mb-12">
                        <h2 className="font-display text-3xl sm:text-4xl font-bold text-brand-navy tracking-tight">
                            Designed Around Our Customers
                        </h2>
                    </div>
                    <div className="grid md:grid-cols-3 gap-6">
                        {VALUES.map(({ icon: Icon, title, desc }) => (
                            <Card key={title} className="rounded-2xl border-slate-100 p-7">
                                <div className="h-11 w-11 rounded-xl bg-brand-soft grid place-items-center">
                                    <Icon className="h-5 w-5 text-brand-royal" strokeWidth={1.75} />
                                </div>
                                <h3 className="mt-5 font-semibold text-slate-900">{title}</h3>
                                <p className="mt-2 text-sm text-slate-600 leading-relaxed">{desc}</p>
                            </Card>
                        ))}
                    </div>
                </div>
            </section>

            {/* ABOUT PREVIEW ------------------------------------------------ */}
            <section id="about" className="bg-slate-50 py-20 sm:py-24">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 grid lg:grid-cols-2 gap-14 items-center">
                    <div className="relative order-2 lg:order-1">
                        <div className="absolute -inset-3 bg-brand-tint/40 rounded-[2rem] rotate-1" aria-hidden />
                        <img
                            src="/home-community.jpg"
                            alt="Group of community members standing together"
                            className="relative rounded-[1.75rem] object-cover w-full h-[380px] shadow-card-hover"
                            loading="lazy"
                        />
                    </div>
                    <div className="order-1 lg:order-2">
                        <span className="inline-block text-xs font-semibold uppercase tracking-widest text-brand-royal">
                            About Lokdhara
                        </span>
                        <h2 className="mt-3 font-display text-3xl sm:text-4xl font-bold text-brand-navy tracking-tight leading-tight">
                            Building Financial Progress Together
                        </h2>
                        <p className="mt-5 text-slate-600 leading-relaxed">
                            Lokdhara Microfinance is focused on providing structured and accessible
                            financial services through transparent processes, clear records and
                            customer-oriented support.
                        </p>
                        <Button className="mt-7 rounded-full h-11 px-6 bg-brand-navy hover:bg-brand-royal text-white"
                            onClick={() => scrollTo("#contact")}
                            data-testid="about-more-btn"
                        >
                            Know More About Us <ArrowRight className="h-4 w-4 ml-2" />
                        </Button>
                    </div>
                </div>
            </section>

            {/* CONTACT ------------------------------------------------------ */}
            <section id="contact" className="py-20 sm:py-24">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 grid lg:grid-cols-2 gap-12">
                    <div>
                        <h2 className="font-display text-3xl sm:text-4xl font-bold text-brand-navy tracking-tight leading-tight">
                            We&apos;re here to help you
                        </h2>
                        <p className="mt-3 text-slate-600">
                            Reach us through any of the channels below.
                        </p>
                        <div className="mt-8 space-y-5">
                            {[
                                { icon: Phone, label: "Call Us", value: "+91 93592 86008", href: "tel:+919359286008" },
                                { icon: Mail, label: "Email Us", value: "support@lokdhara.co", href: "mailto:support@lokdhara.co" },
                                { icon: MapPin, label: "Visit Us",
                                  value: "Sai Sanskar Apartment, Kachare Galli, Dange Chauk Road, Ishwarpur, 415409" },
                            ].map((c) => (
                                <div key={c.label} className="flex items-start gap-3">
                                    <div className="h-11 w-11 rounded-xl bg-brand-soft grid place-items-center shrink-0">
                                        <c.icon className="h-5 w-5 text-brand-royal" strokeWidth={1.75} />
                                    </div>
                                    <div className="min-w-0">
                                        <div className="text-xs uppercase tracking-wider font-semibold text-slate-500">
                                            {c.label}
                                        </div>
                                        {c.href ? (
                                            <a href={c.href} className="text-slate-800 font-medium hover:text-brand-royal">
                                                {c.value}
                                            </a>
                                        ) : (
                                            <div className="text-slate-800 font-medium">{c.value}</div>
                                        )}
                                    </div>
                                </div>
                            ))}
                            <div className="flex items-start gap-3" data-testid="home-office-hours">
                                <div className="h-11 w-11 rounded-xl bg-brand-soft grid place-items-center shrink-0">
                                    <Clock className="h-5 w-5 text-brand-royal" strokeWidth={1.75} />
                                </div>
                                <div className="min-w-0">
                                    <div className="text-xs uppercase tracking-wider font-semibold text-slate-500">
                                        Office Hours
                                    </div>
                                    <div className="text-slate-800 font-medium leading-snug">
                                        {OFFICE_HOURS_LINES.map((l) => (
                                            <div key={l}>{l}</div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <Card className="rounded-2xl border-slate-100 p-6 sm:p-8">
                        <h3 className="font-display text-xl font-bold text-brand-navy">Send us a Message</h3>
                        <form
                            className="mt-5 space-y-4"
                            onSubmit={(e) => {
                                e.preventDefault();
                                toast.success("Thanks! We'll be in touch soon.");
                            }}
                            data-testid="contact-form"
                        >
                            <div className="grid sm:grid-cols-2 gap-4">
                                <Input placeholder="Full Name" className="h-11 rounded-xl" required />
                                <Input placeholder="Mobile Number" className="h-11 rounded-xl" inputMode="tel" required />
                            </div>
                            <Input placeholder="Email" className="h-11 rounded-xl" type="email" />
                            <Textarea placeholder="Message" rows={4} className="rounded-xl" required />
                            <Button
                                type="submit"
                                className="w-full sm:w-auto bg-brand-royal hover:bg-brand-hover text-white rounded-full h-12 px-8"
                                data-testid="contact-send-btn"
                            >
                                Send Message <ArrowRight className="h-4 w-4 ml-2" />
                            </Button>
                        </form>
                    </Card>
                </div>
            </section>

            {/* CTA ---------------------------------------------------------- */}
            <section className="bg-gradient-to-br from-brand-navy to-brand-royal text-white">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 py-16 sm:py-20 text-center">
                    <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">
                        Let&apos;s build financial progress together.
                    </h2>
                    <div className="mt-8 flex flex-wrap justify-center gap-3">
                        <Button
                            className="bg-white text-brand-navy hover:bg-brand-soft rounded-full h-12 px-6 font-semibold"
                            onClick={() => scrollTo("#contact")}
                            data-testid="cta-contact-btn"
                        >
                            Contact Us
                        </Button>
                        <Button
                            variant="outline"
                            className="rounded-full h-12 px-6 border-white/40 text-white hover:bg-white/10 hover:text-white"
                            onClick={placeholderLoginToast}
                            data-testid="cta-customer-login"
                        >
                            Customer Login
                        </Button>
                    </div>
                </div>
            </section>

            {/* FOOTER ------------------------------------------------------- */}
            <footer className="bg-brand-navy text-slate-300">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 pt-16 pb-8">
                    <div className="grid gap-10 md:grid-cols-2 lg:grid-cols-5">
                        <div className="lg:col-span-2">
                            <BrandMark size={40} variant="white" />
                            <p className="mt-5 text-sm leading-relaxed max-w-md text-slate-400">
                                Lokdhara Microfinance is focused on providing structured and accessible
                                financial services through transparent processes, clear records and
                                customer-oriented support.
                            </p>
                            <div className="mt-5 flex gap-3">
                                {[Facebook, Instagram, Linkedin, MessageCircle].map((Icon, i) => (
                                    <a key={i} href="#top"
                                        className="h-9 w-9 rounded-lg bg-white/10 hover:bg-white/20 grid place-items-center transition"
                                        aria-label={`social-${i}`}
                                    >
                                        <Icon className="h-4 w-4" />
                                    </a>
                                ))}
                            </div>
                        </div>
                        {Object.entries(FOOTER_LINKS).map(([col, items]) => (
                            <div key={col}>
                                <div className="text-white font-semibold text-sm mb-4">{col}</div>
                                <ul className="space-y-2 text-sm">
                                    {items.map((item) => (
                                        <li key={item}>
                                            <button
                                                onClick={() => scrollTo("#contact")}
                                                className="hover:text-white transition text-left"
                                            >
                                                {item}
                                            </button>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        ))}
                    </div>

                    <p className="mt-12 text-xs text-slate-400 leading-relaxed max-w-3xl">
                        Product availability, eligibility, rates and terms are subject to approval and
                        applicable requirements.
                    </p>

                    <div className="mt-8 pt-6 border-t border-white/10 flex flex-wrap items-center justify-between gap-3 text-xs text-slate-400">
                        <div>© {new Date().getFullYear()} Lokdhara Microfinance. All rights reserved.</div>
                        <div className="flex items-center gap-5">
                            <button onClick={() => scrollTo("#contact")} className="hover:text-white">Privacy Policy</button>
                            <button onClick={() => scrollTo("#contact")} className="hover:text-white">Terms &amp; Conditions</button>
                            <Link
                                to="/login"
                                className="text-slate-300 hover:text-white underline underline-offset-4"
                                data-testid="footer-staff-login"
                            >
                                Staff Login
                            </Link>
                        </div>
                    </div>
                </div>
            </footer>
        </div>
    );
}
